// Page Synthese 2/2 - Analyse detaillee avec tableaux - V4
// Tableaux : Q1 Risques par metier (Total + Moyenne) + Q2 Management
// Top 3 en vert en haut des tableaux
// Multi-metiers : Tabs pour naviguer entre metiers (colonne Risques)

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { api } from '../services/api'
import { getRiskRank, getManagementRank } from '../lib/theme-ranks'

interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Interface pour les scores par thème
interface ThemeScore {
  theme: string
  total: number
  count: number
  moyenne: number
  maxPossible: number // count * 3
}

export default function Synthese2Page() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Charger les données du diagnostic (depuis state ou IndexedDB)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [questionsQ1, setQuestionsQ1] = useState<any[]>([])
  const [questionsQ2, setQuestionsQ2] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Generer les etapes de progression avec info multi-metiers
  // Synthese2 = tous les metiers sont completes
  const steps = useDiagnosticSteps('resultats', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress, 'resultats-analyse')

  // Charger les donnees
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)
      setError(null)

      try {
        const [diagnostic, q1, q2] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getQuestionsQ1(),
          api.getQuestionsQ2(),
        ])

        const reponsesData = (diagnostic.reponses || []).map((r: any) => ({
          ...r,
          metierId: r.metier?.code || r.metierId,
        }))
        setReponses(reponsesData)
        setQuestionsQ1(q1)
        setQuestionsQ2(q2)

        if (reponsesData.length === 0) {
          setError('Aucune réponse trouvée pour ce diagnostic')
        }
      } catch (err: any) {
        console.error('Erreur chargement resultats:', err)
        const detail = err?.message || err?.status || String(err)
        setError(`Impossible de charger les résultats (${detail})`)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Calculer les scores Q1 par métier et par THEME REEL (ex: "Risque électrique", "Coupure ou sectionnement")
  const classementQ1ByMetier = useMemo(() => {
    const result = new Map<string, ThemeScore[]>()

    metierIds.forEach((metierId) => {
      const themeScoresMap = new Map<string, ThemeScore>()

      const metierReponses = reponses.filter(r => r.metierId === metierId)

      metierReponses.forEach((reponse) => {
        const question = questionsQ1.find(q => q.id === reponse.questionId)
        if (question) {
          // Utiliser themeQuestion (thème détaillé) depuis l'API, sinon fallback sur theme.nom
          const themeName = question.themeQuestion
            || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
            || 'Thème inconnu'

          if (!themeScoresMap.has(themeName)) {
            themeScoresMap.set(themeName, {
              theme: themeName,
              total: 0,
              count: 0,
              moyenne: 0,
              maxPossible: 0,
            })
          }

          const current = themeScoresMap.get(themeName)!
          current.total += reponse.valeur
          current.count += 1
          current.maxPossible = current.count * 3
          current.moyenne = current.count > 0 ? current.total / current.count : 0
        }
      })

      // US-30 : tri par pourcentage décroissant, tie-break par rang OPPBTP croissant
      // (les dénominateurs diffèrent — comparer en absolu fausserait le classement).
      const items = Array.from(themeScoresMap.values())
      items.sort((a, b) => {
        const pa = a.maxPossible > 0 ? a.total / a.maxPossible : 0
        const pb = b.maxPossible > 0 ? b.total / b.maxPossible : 0
        if (pa !== pb) return pb - pa
        return getRiskRank(a.theme) - getRiskRank(b.theme)
      })

      result.set(metierId, items)
    })

    return result
  }, [metierIds, reponses, questionsQ1])

  // Calculer les scores Q2 par THEME REEL (ex: "évaluation des risques (DUER)", "accueil nouveaux arrivants")
  const classementQ2 = useMemo(() => {
    const themeScoresMap = new Map<string, ThemeScore>()

    // Q2 n'a pas de metierId, prendre toutes les réponses sans metierId
    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question) {
        // Utiliser themeQuestion (thème détaillé) depuis l'API, sinon fallback sur theme.nom
        const themeName = question.themeQuestion
          || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
          || 'Thème inconnu'

        if (!themeScoresMap.has(themeName)) {
          themeScoresMap.set(themeName, {
            theme: themeName,
            total: 0,
            count: 0,
            moyenne: 0,
            maxPossible: 0,
          })
        }

        const current = themeScoresMap.get(themeName)!
        current.total += reponse.valeur
        current.count += 1
        current.maxPossible = current.count * 3
        current.moyenne = current.count > 0 ? current.total / current.count : 0
      }
    })

    // US-30 : tri par pourcentage décroissant, tie-break par rang OPPBTP management croissant.
    const items = Array.from(themeScoresMap.values())
    items.sort((a, b) => {
      const pa = a.maxPossible > 0 ? a.total / a.maxPossible : 0
      const pb = b.maxPossible > 0 ? b.total / b.maxPossible : 0
      if (pa !== pb) return pb - pa
      return getManagementRank(a.theme) - getManagementRank(b.theme)
    })

    return items
  }, [reponses, questionsQ2])

  // Navigation
  const handleGoToCasques = () => {
    trackEvent('voir le niveau de prévention')
    navigate(`/diagnostic/${diagnosticId}/casques`, {
      state: diagnosticState,
    })
  }

  const handleGoBack = () => {
    navigate(`/diagnostic/${diagnosticId}/resultats`, {
      state: diagnosticState,
    })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement de l'analyse...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  if (error) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <Card className="max-w-md w-full">
            <CardContent>
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-destructive mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h2 className="text-xl font-semibold text-foreground mb-2">Erreur</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => navigate('/')}>
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleGoBack}
      onNext={handleGoToCasques}
      backLabel="Scores et radars"
      nextLabel="Voir le niveau de prévention"

      diagnosticState={diagnosticState}
    >
      {/* Header de page */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Résultats - Analyse détaillée</h1>
        <p className="text-sm text-muted-foreground mt-1">Classement des thèmes par score (les 3 meilleurs en vert, les 3 plus faibles en rouge)</p>
      </div>

      {/* Grille côte à côte : Risques (Q1) | Management (Q2) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonne Risques (Q1) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </span>
                Risques
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {metierIds.length === 1 ? (
                // 1 seul métier → table directe sans tabs
                <ThemeTable items={classementQ1ByMetier.get(metierIds[0]) || []} />
              ) : (
                // Multi-métiers → Tabs
                <Tabs defaultValue={metierIds[0]}>
                  <TabsList className={`w-full grid grid-cols-${Math.min(metierIds.length, 4)}`}>
                    {metierIds.map((metierId, index) => (
                      <TabsTrigger
                        key={metierId}
                        value={metierId}
                        className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs"
                      >
                        {metiersSelectionnes[index]?.nom || metierId}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {metierIds.map((metierId) => (
                    <TabsContent key={metierId} value={metierId} className="mt-3">
                      <ThemeTable items={classementQ1ByMetier.get(metierId) || []} />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </CardContent>
          </Card>

          {/* Colonne Management (Q2) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </span>
                Management
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ThemeTable items={classementQ2} />
            </CardContent>
          </Card>
        </div>
    </DiagnosticLayout>
  )
}

// Composant Table réutilisé pour Q1 et Q2
function ThemeTable({ items }: { items: ThemeScore[] }) {
  // Story 13 / US-30 :
  //  • Le classement est trié par pourcentage décroissant (les dénominateurs
  //    diffèrent : 12 ou 9), donc index 0 = meilleur %, dernier = pire %.
  //  • Tie-break par rang OPPBTP croissant déjà appliqué en amont dans le sort
  //    des items (cf. classementQ1ByMetier / classementQ2). Les sorts internes
  //    de topThemes/flopThemes étant stables (ES2019+), le tie-break se propage.
  //  • Cap STRICT : maximum 3 verts (top3) et 3 rouges (flop3) par colonne.
  //  • Un thème ≥ 70% n'est jamais marqué flop, même s'il fait partie des
  //    derniers du classement (évite que tout passe en rouge sur un diag bon).
  //  • Un thème ne peut jamais être à la fois top et flop.
  const FLOP_THRESHOLD_PERCENT = 70

  const ranked = useMemo(() => items.map(item => ({
    theme: item.theme,
    percent: item.maxPossible > 0 ? (item.total / item.maxPossible) * 100 : 0,
  })), [items])

  const topThemes = useMemo(() => {
    const set = new Set<string>()
    if (ranked.length === 0) return set
    const sorted = [...ranked].sort((a, b) => b.percent - a.percent)
    const cap = Math.min(3, sorted.length)
    for (let i = 0; i < cap; i++) {
      set.add(sorted[i].theme)
    }
    return set
  }, [ranked])

  const flopThemes = useMemo(() => {
    const set = new Set<string>()
    const eligible = ranked
      .filter(it => !topThemes.has(it.theme) && it.percent < FLOP_THRESHOLD_PERCENT)
      .sort((a, b) => a.percent - b.percent)
    const cap = Math.min(3, eligible.length)
    for (let i = 0; i < cap; i++) {
      set.add(eligible[i].theme)
    }
    return set
  }, [ranked, topThemes])

  return (
    <Table className="table-fixed w-full">
      <TableHeader>
        <TableRow className="border-b-2">
          <TableHead className="text-xs uppercase tracking-wide w-[calc(100%-7rem)]">Thème</TableHead>
          <TableHead className="text-xs uppercase tracking-wide text-right w-[7rem]">Score obtenu</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item, index) => {
          const isTop3 = topThemes.has(item.theme)
          const isFlop = flopThemes.has(item.theme)
          const rank = index + 1
          const percent = item.maxPossible > 0 ? (item.total / item.maxPossible) * 100 : 0

          return (
            <TableRow
              key={item.theme}
              className={
                isTop3 ? 'bg-primary/5 hover:bg-primary/10'
                : isFlop ? 'bg-red-50 hover:bg-red-100'
                : ''
              }
            >
              <TableCell className="py-3 min-w-0">
                <div className="flex items-start gap-2 min-w-0">
                  {/* Badge de rang : limité aux 3 premiers (au-delà = ex-aequo top, pas de numéro) */}
                  {isTop3 && rank <= 3 && (
                    <Badge className="size-5 shrink-0 justify-center p-0 text-[10px] mt-0.5">
                      {rank}
                    </Badge>
                  )}
                  <span className={`text-sm break-words ${
                    isTop3 ? 'font-medium text-foreground'
                    : isFlop ? 'font-medium text-red-700'
                    : 'text-foreground'
                  }`}>
                    {item.theme}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-3 text-right">
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-sm font-semibold ${
                    isTop3 ? 'text-primary'
                    : isFlop ? 'text-red-700'
                    : 'text-foreground'
                  }`}>
                    {item.total}/{item.maxPossible}
                  </span>
                  <Progress value={percent} className="h-1 w-14" />
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
